import { useState, useEffect } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { toast } from 'sonner';
import { Loader2, ArrowLeft, CheckCircle2, XCircle, Mail } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const VerifyEmailPage = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [status, setStatus] = useState('loading'); // loading | success | error
  const [errorMessage, setErrorMessage] = useState('');
  const [resendEmail, setResendEmail] = useState('');
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSent, setResendSent] = useState(false);

  useEffect(() => {
    if (!token) {
      setStatus('error');
      setErrorMessage('Aucun token de vérification fourni.');
      return;
    }

    const verifyEmail = async () => {
      try {
        const res = await axios.get(`${API_URL}/api/auth/verify-email?token=${token}`);
        setStatus('success');
        toast.success(res.data.message || 'Email vérifié avec succès !');
      } catch (error) {
        setStatus('error');
        setErrorMessage(
          error.response?.data?.detail || 'Lien de vérification invalide ou expiré.'
        );
      }
    };

    verifyEmail();
  }, [token]);

  const handleResend = async (e) => {
    e.preventDefault();
    if (!resendEmail) {
      toast.error('Veuillez saisir votre adresse email');
      return;
    }

    setResendLoading(true);
    try {
      await axios.post(`${API_URL}/api/auth/resend-verification`, { email: resendEmail });
      setResendSent(true);
      toast.success('Un nouveau lien de vérification a été envoyé.');
    } catch (error) {
      if (error.response?.status === 429) {
        toast.error('Trop de requêtes. Réessayez dans une minute.');
      } else {
        toast.error(error.response?.data?.detail || 'Une erreur est survenue');
      }
    } finally {
      setResendLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] flex items-center justify-center px-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-3xl font-serif">Vérification email</CardTitle>
          <CardDescription>
            {status === 'loading' && 'Vérification en cours...'}
            {status === 'success' && 'Votre email a été vérifié'}
            {status === 'error' && 'La vérification a échoué'}
          </CardDescription>
        </CardHeader>

        {status === 'loading' && (
          <CardContent className="text-center py-8">
            <Loader2 className="h-12 w-12 animate-spin text-[#D62828] mx-auto" />
            <p className="text-sm text-muted-foreground mt-4">
              Veuillez patienter pendant la vérification de votre email...
            </p>
          </CardContent>
        )}

        {status === 'success' && (
          <>
            <CardContent className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                <CheckCircle2 className="h-8 w-8 text-green-600 dark:text-green-400" />
              </div>
              <p className="text-sm text-muted-foreground">
                Votre adresse email a été vérifiée avec succès. Vous pouvez maintenant
                profiter pleinement de votre compte InfoFlow.
              </p>
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              <Link to="/login" className="w-full">
                <Button className="w-full bg-[#D62828] hover:bg-[#B91C1C]">
                  Se connecter
                </Button>
              </Link>
              <Link to="/" className="text-sm text-[#D62828] hover:underline flex items-center gap-1 justify-center">
                <ArrowLeft className="h-4 w-4" />
                Retour à l'accueil
              </Link>
            </CardFooter>
          </>
        )}

        {status === 'error' && (
          <>
            <CardContent className="text-center space-y-4">
              <div className="mx-auto w-16 h-16 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                <XCircle className="h-8 w-8 text-red-600 dark:text-red-400" />
              </div>
              <p className="text-sm text-muted-foreground">
                {errorMessage}
              </p>

              {!resendSent ? (
                <form onSubmit={handleResend} className="space-y-3 pt-2">
                  <p className="text-sm font-medium">Renvoyer un lien de vérification :</p>
                  <div className="space-y-2">
                    <Label htmlFor="resend-email">Adresse email</Label>
                    <Input
                      id="resend-email"
                      type="email"
                      value={resendEmail}
                      onChange={(e) => setResendEmail(e.target.value)}
                      placeholder="votre@email.com"
                      required
                    />
                  </div>
                  <Button
                    type="submit"
                    variant="outline"
                    className="w-full"
                    disabled={resendLoading}
                  >
                    {resendLoading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    <Mail className="mr-2 h-4 w-4" />
                    Renvoyer le lien
                  </Button>
                </form>
              ) : (
                <div className="space-y-2 pt-2">
                  <div className="mx-auto w-12 h-12 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <Mail className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Un nouveau lien a été envoyé à <strong>{resendEmail}</strong>.
                    Vérifiez votre boîte mail.
                  </p>
                </div>
              )}
            </CardContent>
            <CardFooter className="flex flex-col gap-3">
              <Link to="/login" className="text-sm text-[#D62828] hover:underline flex items-center gap-1 justify-center">
                <ArrowLeft className="h-4 w-4" />
                Retour à la connexion
              </Link>
            </CardFooter>
          </>
        )}
      </Card>
    </div>
  );
};

export default VerifyEmailPage;
